export const CourseAudience = {
  None: 0,
  SocialWorkers: 1,
  EducationWorkers: 2,
  HealthcareWorkers: 4,
  ParentsAndCarers: 8,
  Other: 32,
  SocialCareWorkers: 64,
} as const;

export type CourseAudience = (typeof CourseAudience)[keyof typeof CourseAudience];

export const CourseLevel = {
  Unknown: 0,
  Introduction: 1,
  Intermediate: 2,
  Advanced: 3,
  Expert: 4,
} as const;

export type CourseLevel = (typeof CourseLevel)[keyof typeof CourseLevel];

export const CourseVersionStatus = {
  Draft: 0,
  Published: 1,
  Archived: 2,
} as const;

export type CourseVersionStatus = (typeof CourseVersionStatus)[keyof typeof CourseVersionStatus];

export const LearningPathState = {
  Draft: 0,
  Published: 1,
  Archived: 2,
} as const;

export type LearningPathState = (typeof LearningPathState)[keyof typeof LearningPathState];

/** How learners move between a learning path's courses. */
export const LearningPathNavigationMode = {
  /** Any course in the path can be opened, in any order. */
  Free: 0,
  /** Each course opens once the one before it is complete; the first is always open. */
  Sequential: 1,
} as const;

export type LearningPathNavigationMode =
  (typeof LearningPathNavigationMode)[keyof typeof LearningPathNavigationMode];

/** How far a learner has got with a course in a learning path, across every version of the course. */
export const LearnerCourseStatus = {
  NotStarted: 0,
  InProgress: 1,
  Completed: 2,
} as const;

export type LearnerCourseStatus = (typeof LearnerCourseStatus)[keyof typeof LearnerCourseStatus];

/** Where a learner is with booking an instructor-led course in a learning path (LMS-127). */
export const IltBookingStatus = {
  NotBooked: 0,
  /** Holds a booking on a session whose attendance isn't marked yet. */
  Booked: 1,
  /** Marked Attended on one of its sessions, which completes the course. */
  Attended: 2,
} as const;

export type IltBookingStatus = (typeof IltBookingStatus)[keyof typeof IltBookingStatus];

/** How far a learner has got with a learning path they are enrolled on, as the path records it. */
export const LearningPathProgressStatus = {
  NotStarted: 0,
  InProgress: 1,
  Completed: 2,
} as const;

export type LearningPathProgressStatus =
  (typeof LearningPathProgressStatus)[keyof typeof LearningPathProgressStatus];

/** Where a learner is with a learning path they are enrolled on or have been assigned, in the admin report. */
export const LearnerPathReportStatus = {
  /** Assigned by an admin and never started. */
  Assigned: 0,
  NotStarted: 1,
  InProgress: 2,
  Completed: 3,
  /** Assigned and started, but the learner has since left the path. */
  Unenrolled: 4,
} as const;

export type LearnerPathReportStatus =
  (typeof LearnerPathReportStatus)[keyof typeof LearnerPathReportStatus];

/** Whether a course in a learning path is available to learners. */
export const PathCourseAvailability = {
  Published: 0,
  /** A draft, or a course that is offline or has been unpublished. */
  Unpublished: 1,
  Archived: 2,
} as const;

export type PathCourseAvailability =
  (typeof PathCourseAvailability)[keyof typeof PathCourseAvailability];

export const ScormIngestionStatus = {
  NotRequired: 0,
  Pending: 1,
  Processing: 2,
  Completed: 3,
  Failed: 4,
} as const;

export type ScormIngestionStatus = (typeof ScormIngestionStatus)[keyof typeof ScormIngestionStatus];

export const ScormStructureType = {
  Unknown: 0,
  MultiSco: 1,
  SingleScoLinear: 2,
  SingleScoAdaptLike: 3,
} as const;

export type ScormStructureType = (typeof ScormStructureType)[keyof typeof ScormStructureType];

/** Online (SCORM) or instructor-led training (ILT). Matches the API's CourseDeliveryType. */
export const CourseDeliveryType = {
  Online: 0,
  InstructorLed: 1,
} as const;

export type CourseDeliveryType = (typeof CourseDeliveryType)[keyof typeof CourseDeliveryType];

/** The unit a course's length is entered and shown in. The API always stores minutes. */
export const CourseLengthUnit = {
  Minutes: 0,
  Hours: 1,
  Days: 2,
} as const;

export type CourseLengthUnit = (typeof CourseLengthUnit)[keyof typeof CourseLengthUnit];
